import { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import search from 'assets/img/search.svg';
import KEYBOARD_KEYS from 'constants/keyboard';
import { step } from 'pages/Authorize/steps';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import payrollProviderToLocalLogosMap from 'constants/payrollProviderToLocalLogosMap';
import { isAssistedImplementationKind } from '@finch-api/common/dist/internal/connect/authorize';
import { useRegisterStep } from 'services/register-step';

import styles from './SelectProvider.module.scss';
import { AnalyticsEventName, track } from 'utils/analytics';
import { useStagedConfigStore } from '../useStagedConfig';
import { GoBackButton } from 'components/Button/GoBackButton';
import { PayrollProviderConfig } from '@finch-api/common/dist/internal/connect/validate';
import Input from 'components/Input/Input';
import { useTrackConversionStep } from '../../../services/track-conversion';

const PayrollProviderBlock = styled.div`
  display: flex;
  align-items: center;
`;

const SelectProvider = ({
  setCurrentStep,
  nextStep,
  setPayrollProvider,
  setProviderImplementation,
  currentBrowserSessionKey,
  providersConfig,
  payrollProvider,
  prevStep,
  providerId,
}: AuthorizeContextValue) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const registerStep = useRegisterStep();
  const trackConversion = useTrackConversionStep();
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      trackConversion({
        action: 'display',
      });
      registerStep({ step: step.SELECT_PROVIDER });
      mounted.current = true;
    }
  }, [trackConversion]);
  const stagedConfig = useStagedConfigStore((state) => state.stagedConfig);

  const selectProvider = async (payrollProvider: PayrollProviderConfig) => {
    setPayrollProvider(payrollProvider);

    track(AnalyticsEventName.SelectedProvider, {
      payrollProviderId: payrollProvider.id,
      currentBrowserSessionKey,
    });
    trackConversion({
      action: 'submit',
    });
    registerStep({
      step: step.SELECT_PROVIDER,
      providerId: payrollProvider.id,
      providerImplementationId: payrollProvider.implementations[0].id,
    });

    const isManualProvider =
      payrollProvider.implementations.length === 1 &&
      isAssistedImplementationKind(payrollProvider.implementations[0].kind);

    if (isManualProvider) {
      setCurrentStep(step.MANUAL_SIGN_IN);
      setProviderImplementation(payrollProvider.implementations[0]);
      return;
    }

    nextStep();
  };

  const onClick = (payrollProvider: PayrollProviderConfig) => {
    selectProvider(payrollProvider);
  };

  // Allow user to press the enter/return key to 'click' the payroll provider
  const onEnter = (callback: () => void) => (event: React.KeyboardEvent) => {
    if (event.key !== KEYBOARD_KEYS.ENTER) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    callback();
  };

  const [searchInput, setSearchInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchInput(event.target.value);
    setSelectedIndex(0); // Reset selection when search changes
  };

  const providerConfigList = stagedConfig || providersConfig;

  useEffect(() => {
    if (providerConfigList && providerConfigList.length === 1) {
      selectProvider(providerConfigList[0]);
      return;
    }

    if (providerId && payrollProvider) {
      selectProvider(payrollProvider);
      return;
    }

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // It should be [] same as componentDidMount

  // currently, we can only handle automated and sandbox flow
  // manual flow will be worked on during acf project, and require redesign
  let filteredPayrollProviders = providerConfigList;

  if (searchInput !== '') {
    const lowercaseSearchInput = searchInput.toLowerCase();
    filteredPayrollProviders = _.filter(
      filteredPayrollProviders,
      (payrollProvider) =>
        payrollProvider.displayName
          .toLowerCase()
          .includes(lowercaseSearchInput),
    );
  }

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!filteredPayrollProviders || filteredPayrollProviders.length === 0) {
      return;
    }

    const providers = filteredPayrollProviders; // Type guard satisfaction
    let newIndex = selectedIndex;

    switch (event.key) {
      case KEYBOARD_KEYS.ARROW_DOWN:
        event.preventDefault();
        newIndex = selectedIndex < providers.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(newIndex);
        break;
      case KEYBOARD_KEYS.ARROW_UP:
        event.preventDefault();
        newIndex = selectedIndex > 0 ? selectedIndex - 1 : providers.length - 1;
        setSelectedIndex(newIndex);
        break;
      case KEYBOARD_KEYS.ENTER:
        event.preventDefault();
        if (providers[selectedIndex]) {
          selectProvider(providers[selectedIndex]);
        }
        break;
      case KEYBOARD_KEYS.TAB:
        setSelectedIndex(-1);
        break;
    }

    // Scroll selected item into view
    if (newIndex !== selectedIndex) {
      setTimeout(() => {
        const selectedElement = document.querySelector(
          `[data-testid="provider"]:nth-child(${newIndex + 1})`,
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 0);
    }
  };

  // Auto-focus single result
  useEffect(() => {
    if (filteredPayrollProviders && filteredPayrollProviders.length === 1) {
      setSelectedIndex(0);
    }
  }, [filteredPayrollProviders]);

  const payrollProvidersToDisplay =
    filteredPayrollProviders && filteredPayrollProviders.length > 0 ? (
      filteredPayrollProviders.map((payrollProvider, index) => (
        <button
          data-testid="provider"
          className={`${styles.payrollProvider} ${
            index === selectedIndex ? styles.selected : ''
          }`}
          key={payrollProvider.id}
          onClick={() => onClick(payrollProvider)}
          onKeyDown={onEnter(() => onClick(payrollProvider))}
          onMouseEnter={() => setSelectedIndex(-1)}
          onMouseLeave={() => setSelectedIndex(-1)}
        >
          <PayrollProviderBlock>
            <img
              src={payrollProviderToLocalLogosMap[payrollProvider.id]?.logo}
              className={styles.logo}
              alt=""
            />
            <div>{payrollProvider.displayName}</div>
          </PayrollProviderBlock>
        </button>
      ))
    ) : (
      <div className={styles.emptySearchResults}>
        <p>No provider found.</p>
      </div>
    );

  return (
    <div className={styles.container}>
      <GoBackButton onClick={prevStep} />
      <div className={styles.header}>Select your provider</div>
      <div style={{ margin: '0 24px 16px' }}>
        <Input
          ref={searchInputRef}
          startAdornment={<img src={search} className={styles.icon} alt="" />}
          label="Search"
          invalid={false}
          value={searchInput}
          onChange={onChange}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className={styles.payrolls}>{payrollProvidersToDisplay}</div>
    </div>
  );
};

export default withAuthorizeContext(SelectProvider);
